import {useKeyPress, Button} from '@miro-site/core'
import React, {useEffect, useRef, useState} from 'react'
import styled, {css} from 'styled-components'
import SearchDropdown, {StatusMessages} from './components/SearchDropdown'

export type SearchResult = {
  title: string
  description: string
  preview?: string
  slug: string
  href: string
}

export interface SearchProps {
  onSelectTemplate?: (template: SearchResult) => void
  onFocusChanged?: (isFocused: boolean) => void
  onSearch: (term: string) => void
  isSearching?: boolean
  searchResult?: SearchResult[]
  className?: string
  statusMessages: StatusMessages
  onItemHover?: (itemIndexHover: number) => void
}

export interface SearchWrapperProps {
  isFocused: boolean
}

export interface InputProps {
  isFocused?: boolean
}

export interface SvgCloseIconWrapper {
  onKeyDown: (e: KeyboardEvent) => void
}

const Wrapper = styled.div`
  position: relative;
  box-sizing: content-box;
  z-index: 3;

  ${({theme}) =>
    theme.normalizeStyles({
      paddingTop: 7,
      height: 40,
      paddingBottom: 7,
      paddingLeft: 0,
      paddingRight: 0,
    })}
`

const Input = styled.input<InputProps>`
  ${({theme, isFocused}) =>
    theme.normalizeStyles({
      color: 'primaryIndigo',
      border: 'none',
      outline: 'none',
      width: [isFocused ? '100%' : 44, '100%', '100%', '100%'],
      background: isFocused ? 'none' : ['none', '#F5F5F7', '#F5F5F7', '#F5F5F7'],
      fontSize: [isFocused ? 16 : 0, 16, 16, 16],
      paddingLeft: [isFocused ? 36 : 0, 46, 46, 46],
      paddingRight: [isFocused ? 36 : 0, 46, 46, 46],
      height: 40,
      borderRadius: 100,
    })}
`

export const SearchWrapper = styled.div<SearchWrapperProps>`
  position: absolute;

  ${props => {
    if (props.isFocused) {
      return props.theme.normalizeStyles({
        borderRadius: 16,
        left: 0,
        right: 0,
        top: 0,
        boxShadow: '0px 8px 44px rgba(5, 0, 56, 0.16)',
        background: '#FFFFFF',
      })
    } else {
      return props.theme.normalizeStyles({
        left: 0,
        right: 0,
        paddingLeft: 0,
        paddingRight: 0,
      })
    }
  }}
`

const SvgSearchIcon = styled.svg`
  position: absolute;
  cursor: pointer;
  top: 50%;

  ${({theme}) =>
    theme.normalizeStyles({
      width: 24,
      height: 24,
      marginTop: -12,
    })};
`

const SvgCloseIconWrapper = styled(Button)<SvgCloseIconWrapper>`
  position: absolute;
  cursor: pointer;
  top: 50%;
  line-height: 1px;

  ${({theme}) =>
    theme.normalizeStyles({
      width: 24,
      height: 24,
      marginTop: -12,
    })};

  &:focus {
    ${({theme}) => theme.normalizeStyles({...theme.focus.outlines.withOffset})}
  }
`

const SearchInputWrapper = styled.div<{isFocused?: boolean}>`
  position: relative;
  line-height: 0;

  ${({theme, isFocused}) =>
    css`
      ${isFocused &&
      theme.normalizeStyles({
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: [10, 34, 34, 34],
        paddingRight: [10, 34, 34, 34],
      })}

      ${SvgSearchIcon} {
        ${theme.normalizeStyles({
          left: isFocused ? [10, 34, 34, 34] : 10,
          zIndex: isFocused ? 1 : [-1, 1, 1, 1],
        })}
      }

      ${SvgCloseIconWrapper} {
        ${theme.normalizeStyles({
          right: isFocused ? [10, 34, 34, 34] : 10,
        })}
      }
    `}
`

const Search: React.FC<SearchProps> = props => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const isEscapePressed = useKeyPress('Escape')

  const [isFocused, setFocused] = useState(false)

  const [value, setValue] = useState('')

  const [selected, setSelected] = useState(0)

  const handleClick = (e: CustomEvent) => {
    if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
      setFocused(false)
    }
  }

  useEffect(() => {
    if (props.onFocusChanged) {
      props.onFocusChanged(isFocused)
    }

    if (isFocused) {
      document.addEventListener('click', handleClick as EventListener, true)
      window.addEventListener('focusin', onFocusDown)

      return () => {
        document.removeEventListener('click', handleClick as EventListener, true)
        document.removeEventListener('keydown', onFocusDown)
      }
    }
  }, [isFocused])

  const onFocusDown = function () {
    if (wrapperRef.current && !wrapperRef.current.contains(document.activeElement)) {
      setFocused(false)
    }
  }

  useEffect(() => {
    async function performSearch() {
      await props.onSearch(value)
    }

    if (value) {
      performSearch()
    }
  }, [value])

  useEffect(() => {
    if (isFocused && isEscapePressed) {
      setFocused(false)
    }
  }, [isEscapePressed])

  useEffect(() => {
    if (!isFocused && inputRef.current) {
      inputRef.current.blur()
    }
  }, [isFocused])

  useEffect(() => {
    if (!props.searchResult?.length) {
      return
    }

    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [props.searchResult, selected])

  const onKeyDown = function (e: KeyboardEvent) {
    if (e.code === 'ArrowUp' && isFocused) {
      // up
      select(selected - 1)
    }
    if (e.code === 'ArrowDown' && isFocused) {
      // down
      select(selected + 1)
    }
  }

  const select = (activeIndex: number) => {
    if (typeof props.searchResult?.[activeIndex] === 'undefined') {
      return
    }

    setSelected(activeIndex)
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'Enter') {
      setValue('')

      if (inputRef.current) {
        inputRef.current.focus()
      }
    }
  }

  return (
    <Wrapper ref={wrapperRef} className={props.className}>
      <SearchWrapper isFocused={isFocused}>
        <SearchInputWrapper isFocused={isFocused}>
          <SvgSearchIcon
            viewBox="0 0 24 24"
            fill="none"
            onClick={() => {
              setFocused(true)
            }}
          >
            <circle cx="10.2308" cy="10.0879" r="6.23077" stroke="#050038" strokeWidth="2" />
            <path
              d="M15 15.0002L21 21.0002"
              stroke="#050038"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </SvgSearchIcon>
          <Input
            ref={inputRef}
            isFocused={isFocused}
            onFocus={() => {
              setFocused(true)
            }}
            value={value}
            onChange={e => {
              setValue(e.target.value)
            }}
            onBlur={() => {
              if (value.length === 0) setFocused(false)
            }}
            //for autotest
            data-testid="templates-search-input"
          />
          {isFocused && value.length > 0 && (
            <SvgCloseIconWrapper onKeyDown={handleKeyDown}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                onClick={() => {
                  setValue('')

                  if (inputRef.current) {
                    inputRef.current.focus()
                  }
                }}
              >
                <path
                  d="M10.5858 12L4.29289 18.2929C3.90237 18.6834 3.90237 19.3166 4.29289 19.7071C4.68342 20.0976 5.31658 20.0976 5.70711 19.7071L12 13.4142L18.2929 19.7071C18.6834 20.0976 19.3166 20.0976 19.7071 19.7071C20.0976 19.3166 20.0976 18.6834 19.7071 18.2929L13.4142 12L19.7071 5.70711C20.0976 5.31658 20.0976 4.68342 19.7071 4.29289C19.3166 3.90237 18.6834 3.90237 18.2929 4.29289L12 10.5858L5.70711 4.29289C5.31658 3.90237 4.68342 3.90237 4.29289 4.29289C3.90237 4.68342 3.90237 5.31658 4.29289 5.70711L10.5858 12Z"
                  fill="#050038"
                />
              </svg>
            </SvgCloseIconWrapper>
          )}
        </SearchInputWrapper>
        <SearchDropdown
          onSelectTemplate={template => {
            setFocused(false)
            setValue('')

            if (props.onSelectTemplate) {
              props.onSelectTemplate(template)
            }
          }}
          isSearching={props.isSearching}
          searchString={value}
          open={isFocused && value.length > 0}
          searchResult={props.searchResult}
          statusMessages={props.statusMessages}
          activeIndex={selected}
          onItemHover={itemIndexHover => {
            setSelected(itemIndexHover)
          }}
        />
      </SearchWrapper>
    </Wrapper>
  )
}

export default Search
